/**
 * @fileoverview `<CopyButton>` — bouton icône pour copier une valeur.
 *
 * Feedback visuel : icône `Copy` → `Check` (vert success) pendant 2 s après
 * un clic réussi. Aucun toast : l'icône suffit pour un contexte de tableau
 * dense (`ColorSwatch`, `MerchantReference`, `LiquidationNumber`) où un
 * toast serait bruyant.
 *
 * A11y : `aria-label` obligatoire (auto-généré depuis `value` si non fourni),
 * `aria-live="polite"` sur le message de succès pour les lecteurs d'écran.
 */

import { Check, Copy } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { useCopyToClipboard } from '../hooks/use-copy-to-clipboard.ts';
import { cn } from '../lib/utils.ts';

export interface CopyButtonProps {
  /** Valeur brute à copier dans le presse-papier. */
  value: string;
  /**
   * Libellé accessible du bouton — lu par les lecteurs d'écran. Défaut :
   * `Copier <value>`. Fournir un libellé métier plus parlant quand la
   * valeur est technique (ex : `Copier le numéro de liquidation`).
   */
  ariaLabel?: string;
  /**
   * Taille du bouton — hérite des tailles Button. `icon-xs` par défaut
   * (24 px) pour rester discret à côté d'un swatch ou d'une valeur.
   */
  size?: 'icon-xs' | 'icon-sm' | 'icon';
  /** Classe additionnelle pour cas particulier. */
  className?: string;
}

/**
 * Bouton icône copie-vers-presse-papier avec feedback transitoire.
 *
 * @example
 * ```tsx
 * <CopyButton value="#001540" ariaLabel="Copier le code couleur" />
 * <CopyButton value="LIQ-921555" ariaLabel="Copier le numéro de liquidation" />
 * ```
 */
export function CopyButton({
  value,
  ariaLabel,
  size = 'icon-xs',
  className,
}: CopyButtonProps): React.JSX.Element {
  const { copy, copied } = useCopyToClipboard();

  const label = copied
    ? `${ariaLabel ?? `Copier ${value}`} — copié`
    : ariaLabel ?? `Copier ${value}`;

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      aria-label={label}
      onClick={() => void copy(value)}
      className={cn(
        'text-muted-foreground transition-colors',
        copied && 'text-success',
        className,
      )}
    >
      {copied ? (
        <Check aria-hidden="true" />
      ) : (
        <Copy aria-hidden="true" />
      )}
    </Button>
  );
}
